import { RouteLocation, Router } from 'vue-router'
import { ContentData, PageData } from '@rtl/api'
import { Store } from 'vuex'
import { AppState, MEASUREMENT_ACTION, MEASUREMENT_NAMESPACE } from '../store'

export function getMetaFromRaw (metaData: Array<{ key?: string, value?: string }> = []) {
  return metaData.reduce<{ title?: string, meta: Array<Record<string, string>> }>((res, meta) => {
    if (meta.key && meta.value) {
      if (meta.key === 'title') {
        res.title = meta.value
      } else {
        res.meta.push({
          [meta.key.startsWith('og:') || meta.key.startsWith('fb:') || meta.key.startsWith('video:') ? 'property' : 'name']: meta.key,
          content: meta.value
        })
      }
    }
    return res
  }, { meta: [] })
}

export function generateMetaInfo (route: RouteLocation, router: Router, page?: PageData, content?: ContentData, store?: Store<AppState>) {
  const domain = 'https://rtl.hu'
  const link: Array<unknown> = []
  const script: { [key: string]: unknown }[] = []

  // links
  link.push({
    rel: 'canonical',
    href: `${domain}${router.resolve({ query: { oldal: route.query['oldal'] }}).href}`
  })

  /*link.push({
    vmid: 'font-regular',
    href: require('@rtl/ui/src/assets/fonts/RTLUnitedText-Regular.woff2'),
    as: 'font',
    type: 'font/woff2',
    rel: 'preload',
    crossorigin: true
  }, {
    vmid: 'font-bold',
    href: require('@rtl/ui/src/assets/fonts/RTLUnitedText-Bold.woff2'),
    as: 'font',
    type: 'font/woff2',
    rel: 'preload',
    crossorigin: true
  })*/

  // scripts
  script.push({
    vmid: 'logo-schema',
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      url: domain,
      logo: 'https://rtl.hu/rtl-hu-logo.png',
    })
  })

  if (typeof window === 'undefined' || !window?.location.hostname.startsWith('app.')) {
    const dispatchAdoScriptLoaded = () => {
      store?.dispatch(`${MEASUREMENT_NAMESPACE}/${MEASUREMENT_ACTION.ADO_SCRIPT_LOADED}`)
    }
    script.push({
      vmid: 'adocean',
      src: '//hu.adocean.pl/files/js/ado.js',
      async: true,
      defer: true,
      onload: () => dispatchAdoScriptLoaded(),
      onerror: () => dispatchAdoScriptLoaded()
    })
  }

  const airship = store?.getters.getEnv('AIRSHIP')
  if (airship) {
    script.push({
      vmid: 'airship',
      type: 'text/javascript',
      innerHTML: `
        // 86acbd31cd7c09cf30acb66d2fbedc91daa48b86:1659520575.5442133
        !function(n,r,e,t,c){var i,o="Promise"in n,u={then:function(){return u},catch:function(n){
        return n(new Error("Airship SDK Error: Unsupported browser")),u}},s=o?new Promise((function(n,r){i=function(e,t){e?r(e):n(t)}})):u
        ;s._async_setup=function(n){if(o)try{i(null,n(c))}catch(n){i(n)}},n[t]=s;var a=r.createElement("script");a.src=e,a.async=!0,a.id="_uasdk",
        a.rel=t,r.head.appendChild(a)}(window,document,'https://aswpsdkeu.com/notify/v1/ua-sdk.min.js',
          'UA', JSON.parse('${airship}'));
      `,
    })
  }

  /* if (page?.site) {
    script.push({
      vmid: 'series-schema',
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'TVSeries',
        name: page.site.name,
        description: page.site.parameters?.config?.description,
        image: (page.site.parameters?.appearance?.coverImageUrls as any)?.coverImageUrl,
        copyrightYear: page.site.parameters?.nmhhParams?.prodYear ? parseInt(page.site.parameters.nmhhParams.prodYear): undefined,
        director: page.site.parameters?.nmhhParams?.directorName ? {
          '@type': 'Person',
          name: page.site.parameters.nmhhParams.directorName
        } : undefined,
        containsSeason: page.site.seasons?.map(season => ({
          '@type': 'TVSeason',
          name: season
        }))
      })
    })
  } */

  if (content) {
    if (content.contentType !== 'celeb') {
      const contentTitle = content.alternativeTitle?.trim() || content.title?.trim() || ''
      const contentDescription = (content.alternativeLead?.trim() || content.lead?.trim() || '').replace(/<[^>]*>?/gm, '').trim()
      const contentReleaseDate = content.releaseDateRaw

      const images = []
      if (content.leadImageUrls?.leadImageUrl) {
        images.push(content.leadImageUrls.leadImageUrl)
      }

      script.push({
        vmid: 'article-schema',
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: contentTitle,
          image: images,
          datePublished: contentReleaseDate,
          dateModified: content.modifiedTsRaw,
          author: content.authors?.map(author => ({
            '@type': 'Person',
            name: author.name
          })),
          video: content.params?.withVideo ? {
            '@type': 'VideoObject',
            name: contentTitle,
            description: contentDescription || contentTitle,
            ...content.params?.duration ? { duration: `PT${content.params?.duration || 0}S` } : null,
            thumbnailUrl: images.length ? images : [ `https://rtl.hu${require('../assets/img/rtl_placeholder_image.svg')}` ],
            uploadDate: contentReleaseDate,
            embedUrl: content.params?.embedVideoUrl
          } : undefined,
          publisher: {
            '@type': 'Organization',
            name: 'RTL.hu',
            logo: {
              '@type': 'ImageObject',
              url: 'https://rtl.hu/rtl-hu-logo.png',
              width: 456,
              height: 80
            }
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${domain}${router.resolve({}).href}`
          }
        })
      })
    }

    script.push({
      vmid: 'breadcrump-schema',
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Címlap',
            item: 'https://rtl.hu'
          },
          ...content.contentType === 'celeb' ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Sztárok',
              item: 'https://rtl.hu/sztarok'
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: content.title,
              item: 'https://rtl.hu/' + content.url
            }
          ] : [
            ...content.headingTag ? [{
              '@type': 'ListItem',
              position: 2,
              name: content.headingTag.tag,
              item: 'https://rtl.hu' + (content.headingTag.site?.url ?? content.headingTag.url)
            }] : [],
            {
              '@type': 'ListItem',
              position: content.headingTag ? 3 : 2,
              name: content.title,
              item: 'https://rtl.hu/' + content.url
            }
          ]
        ]
      })
    })
  }

  return {
    title: 'RTL.hu',
    ...getMetaFromRaw(page?.metaTags),
    link,
    script
  }
}
